/**
 * Utility functions for tracking where users sign up from.
 *
 * Four pieces of context are stored, and they are read together:
 * 1. `signup_trigger`    - The action that triggered the signup (e.g. "import_club")
 * 2. `signup_source_url` - The normalized URL section (e.g. "/squad-builder")
 * 3. `signup_section`    - The surface it started on (e.g. "Squad Builder")
 * 4. `signup_subsection` - The exact feature reached for (e.g. "see-active-squad")
 *
 * The set of triggers, and the rule for which may overwrite which, live in
 * events/signupTriggers.ts — read that first if you are adding a new one.
 */
import { safeGetItem, safeRemoveItem, safeSetItem } from "@/utils/safeStorage";

import { shouldOverwriteSignupContext } from "./signupTriggers";

const SIGNUP_TRIGGER_KEY = "signup_trigger";
const SIGNUP_SOURCE_URL_KEY = "signup_source_url";
const SIGNUP_SECTION_KEY = "signup_section";
const SIGNUP_SUBSECTION_KEY = "signup_subsection";
const SIGNUP_STORED_AT_KEY = "signup_stored_at";

const SIGNUP_CONTEXT_TTL_MS = 60 * 60 * 1000;

/**
 * Known site sections for URL normalization.
 * More specific paths should come before less specific ones (sorted by length when matching).
 */
const SITE_SECTIONS = [
  "/evolutions/builder",
  "/evolutions/players",
  "/evolutions/my-evolutions",
  "/evolutions",
  "/squad-builder",
  "/players",
  "/sbc",
  "/my-club/players",
  "/my-club",
  "/subscription",
  "/tactics",
  "/login",
  "/profile",
  "/news",
  "/club-import-tutorial",
  "/import-mobile",
  "/contact",
  "/legal",
  "/who-are-we",
  "/meta-rating",
] as const;

/**
 * Normalizes a URL pathname to its base section.
 * E.g., "/evolutions/builder/ruud-gullit/50545748" → "/evolutions/builder"
 */
export const getUrlSection = (pathname: string): string => {
  // Sort by length descending to match longest/most-specific first
  const sortedSections = [...SITE_SECTIONS].sort((a, b) => b.length - a.length);

  for (const section of sortedSections) {
    if (pathname === section || pathname.startsWith(section + "/")) {
      return section;
    }
  }

  // Fallback: return first segment or root
  const firstSegment = "/" + (pathname.split("/")[1] || "");
  return firstSegment || "/";
};

/**
 * Stores the signup context (trigger, URL, section) in localStorage.
 * Call this when opening a login modal or starting a login flow.
 *
 * **Not every call writes.** A signup flow is usually several steps, and the
 * later ones say less about why the user is signing up than the first did — so
 * a continuation trigger (`login-required` and friends) is dropped when a
 * specific intent is already stored. That is what lets the club-import gate's
 * "import_club / Squad Builder" survive the hop to the tutorial's login modal
 * instead of being overwritten by it. The rule itself lives in
 * shouldOverwriteSignupContext; see events/signupTriggers.ts for the reasoning.
 *
 * A skipped call touches nothing at all — not the section, and not the stored-at
 * timestamp, so the original intent keeps its own TTL clock rather than being
 * kept alive indefinitely by later modals.
 */
export const storeSignupContext = (
  trigger: string,
  options?: { section?: string; subsection?: string },
): void => {
  // Read through getStoredSignupContext rather than the key directly: it also
  // applies the TTL, so an expired intent correctly counts as nothing stored
  // and a continuation can take its place.
  const { trigger: stored } = getStoredSignupContext();
  if (!shouldOverwriteSignupContext(trigger, stored)) return;

  safeSetItem(SIGNUP_TRIGGER_KEY, trigger);
  safeSetItem(SIGNUP_SOURCE_URL_KEY, getUrlSection(window.location.pathname));
  safeSetItem(SIGNUP_STORED_AT_KEY, String(Date.now()));
  // section/subsection are optional extra dimensions for the backend-fired
  // user_signed_up event (so it carries the same section/subsection as the
  // section events). Clear any stale values when a gate doesn't provide them,
  // so a later gate can't inherit the previous one's section/subsection.
  if (options?.section) safeSetItem(SIGNUP_SECTION_KEY, options.section);
  else safeRemoveItem(SIGNUP_SECTION_KEY);
  if (options?.subsection)
    safeSetItem(SIGNUP_SUBSECTION_KEY, options.subsection);
  else safeRemoveItem(SIGNUP_SUBSECTION_KEY);
};

/**
 * Reads the stored signup context without clearing it. Returns the raw stored
 * values (no pathname fallback), so callers can tell whether a signup flow was
 * actually initiated. Used to forward the context to the backend via
 * `x-mp-signup-*` headers, where the `user_signed_up` event is now fired.
 */
export const getStoredSignupContext = (): {
  trigger: string | null;
  sourceUrl: string | null;
  section: string | null;
  subsection: string | null;
} => {
  // Lazy expiry: a context older than the TTL is treated as absent (and cleared
  // so it can't be read again). localStorage has no built-in expiry.
  const storedAt = Number(safeGetItem(SIGNUP_STORED_AT_KEY));
  if (!storedAt || Date.now() - storedAt > SIGNUP_CONTEXT_TTL_MS) {
    clearSignupContext();
    return { trigger: null, sourceUrl: null, section: null, subsection: null };
  }

  return {
    trigger: safeGetItem(SIGNUP_TRIGGER_KEY),
    sourceUrl: safeGetItem(SIGNUP_SOURCE_URL_KEY),
    section: safeGetItem(SIGNUP_SECTION_KEY),
    subsection: safeGetItem(SIGNUP_SUBSECTION_KEY),
  };
};

/**
 * Clears the stored signup context. Call once the context has been consumed
 * (forwarded to the backend) on first-time login.
 */
export const clearSignupContext = (): void => {
  safeRemoveItem(SIGNUP_TRIGGER_KEY);
  safeRemoveItem(SIGNUP_SOURCE_URL_KEY);
  safeRemoveItem(SIGNUP_SECTION_KEY);
  safeRemoveItem(SIGNUP_SUBSECTION_KEY);
  safeRemoveItem(SIGNUP_STORED_AT_KEY);
};
